import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    linkedSignal,
    signal,
} from '@angular/core'

import { ZardIconComponent } from '@/styles/components/icon'
import {
    selectItemIconVariants,
    selectItemVariants,
    type ZardSelectItemModeVariants,
    type ZardSelectSizeVariants,
} from '@/styles/components/select/select.variants'
import { mergeClasses, noopFn } from '@/styles/utils/merge-classes'

// Interface to avoid circular dependency
interface SelectHost {
    selectedValue(): string[]
    selectItem(value: string, label: string): void
    navigateTo(): void
    isMultiple?(): boolean
    isMultiselectItemSelected?(value: string): boolean
}

@Component({
    selector: 'z-select-item, [z-select-item]',
    imports: [ZardIconComponent],
    template: `
        @if (isMultipleMode()) {
            <span
                class="z-select-item__checkbox"
                [class.z-select-item__checkbox--checked]="isSelected()"
                aria-hidden="true"
            >
                @if (isSelected()) {
                    <z-icon
                        zType="check"
                        [zStrokeWidth]="strokeWidth()"
                        aria-hidden="true"
                        data-testid="check-icon"
                    />
                }
            </span>
        } @else {
            @if (isSelected()) {
                <span [class]="iconClasses()">
                    <z-icon
                        zType="check"
                        [zStrokeWidth]="strokeWidth()"
                        aria-hidden="true"
                        data-testid="check-icon"
                    />
                </span>
            }
        }
        <span class="truncate">
            <ng-content />
        </span>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        role: 'option',
        tabindex: '-1',
        '[class]': 'classes()',
        '[class.z-select-item--multi]': 'isMultipleMode()',
        '[attr.value]': 'zValue()',
        '[attr.data-disabled]': 'zDisabled() ? "" : null',
        '[attr.data-selected]': 'isSelected() ? "" : null',
        '[attr.aria-selected]': 'isSelected()',
        '(click)': 'onClick()',
        '(mouseenter)': 'onMouseEnter()',
        '(keydown.{tab}.prevent)': 'noopFn',
    },
})
export class ZardSelectItemComponent {
    readonly elementRef = inject(ElementRef<HTMLElement>)

    readonly zValue = input.required<string>()
    readonly zDisabled = input(false, { transform: booleanAttribute })
    readonly class = input<string>('')
    /** When set, used for trigger display instead of full content (e.g. "Name (Role)" for contact options) */
    readonly zTriggerLabel = input<string>('')

    private readonly select = signal<SelectHost | null>(null)
    noopFn = noopFn

    readonly label = linkedSignal<string>(() => {
        const triggerLabel = this.zTriggerLabel()
        if (triggerLabel) return triggerLabel
        const element = this.elementRef.nativeElement
        return (element.textContent ?? element.innerText)?.trim() ?? ''
    })

    readonly zMode = signal<ZardSelectItemModeVariants>('normal')
    readonly zSize = signal<ZardSelectSizeVariants>('default')

    protected readonly classes = computed(() =>
        mergeClasses(
            selectItemVariants({ zMode: this.zMode(), zSize: this.zSize() }),
            this.class(),
        ),
    )

    protected readonly iconClasses = computed(() =>
        mergeClasses(selectItemIconVariants({ zMode: this.zMode(), zSize: this.zSize() })),
    )

    protected readonly strokeWidth = computed(() => (this.zMode() === 'compact' ? 3 : 2))

    protected readonly isSelected = computed(() => {
        const host = this.select()
        if (!host) return false
        const value = this.zValue()
        if (host.isMultiselectItemSelected) {
            return host.isMultiselectItemSelected(value)
        }
        return host.selectedValue().includes(value)
    })

    protected readonly isMultipleMode = computed(() => this.select()?.isMultiple?.() ?? false)

    setSelectHost(selectHost: SelectHost) {
        this.select.set(selectHost)
    }

    onMouseEnter() {
        if (this.zDisabled()) {
            return
        }
        this.select()?.navigateTo()
    }

    onClick() {
        if (this.zDisabled()) {
            return
        }
        this.select()?.selectItem(this.zValue(), this.label())
    }
}
